import React, { useEffect, useRef } from 'react';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { useTrigger } from '@atlassian/post-office-context';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { ApolloClientProvider } from '@atlassian/jira-apollo-multiple-clients/src/main.tsx';
import client from '@atlassian/jira-apollo-gira/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useGetData } from './services/get-data/useGetData.tsx';
import { asyncChangeboardingModalEntryPoint } from './ui/entrypoint.tsx';
import { emitMessageEvent } from './common/utils.tsx';
import { MESSAGE_TRIGGER_ID } from './common/constants.tsx';

const entryPointParams = {};

type PublicFormChangeboardingModalContainerProps = {
	projectId: string;
};

const PublicFormChangeboardingModalRoot = (props: PublicFormChangeboardingModalContainerProps) => {
	return (
		<JSErrorBoundary
			id="public-forms-changeboarding-modal"
			packageName="@atlassian/jira-public-forms-changeboarding-modal"
			teamName="a4t-tanuki"
			fallback="unmount"
		>
			<ApolloClientProvider client={client}>
				<ModalTransition>
					<PublicFormChangeboardingModalContainer {...props} />
				</ModalTransition>
			</ApolloClientProvider>
		</JSErrorBoundary>
	);
};

const PublicFormChangeboardingModalContainer = ({
	projectId,
}: PublicFormChangeboardingModalContainerProps) => {
	const { isActive: isTriggerActive } = useTrigger(MESSAGE_TRIGGER_ID);
	const data = useGetData({
		projectId,
		isEnabled: isTriggerActive && fg('jira_forms_public_access_changeboarding_gate'),
	});
	const hasLoadedEntrypoint = useRef(false);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncChangeboardingModalEntryPoint,
		entryPointParams,
	);

	const shouldLoadModal =
		isTriggerActive &&
		data &&
		!data.projectHasPublicForm &&
		data.isPublicFormEnabledForSite &&
		data.canAdministerProject &&
		data.projectHasFormNavItem;

	useEffect(() => {
		if (data?.projectHasPublicForm || data?.isPublicFormEnabledForSite === false) {
			emitMessageEvent({ action: 'filter' });
		}
	}, [data?.projectHasPublicForm, data?.isPublicFormEnabledForSite]);

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (!hasLoadedEntrypoint.current && shouldLoadModal) {
			if (!expVal('jira_forms_public_access_changeboarding', 'isEnabled', false)) {
				// if in control cohort, snooze the post office message and check again later
				// as experiment gradually rolls out
				emitMessageEvent({ action: 'snooze' });
				return;
			}

			entryPointActions.load();
			hasLoadedEntrypoint.current = true;
		}
	}, [entryPointActions, shouldLoadModal]);

	if (!data || !data.project || !shouldLoadModal) {
		return null;
	}

	const firstFormIdInProject = data.forms?.[0]?.formId;

	return (
		<JiraEntryPointContainer
			entryPointReferenceSubject={entryPointReferenceSubject}
			id="public-forms-changeboarding-modal"
			packageName="@atlassian/jira-public-forms-changeboarding-modal"
			teamName="a4t-tanuki"
			fallback={null}
			errorFallback="unmount"
			runtimeProps={{ firstFormIdInProject, project: data.project }}
		/>
	);
};

export default PublicFormChangeboardingModalRoot;
